import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { callFunctionSafe } from "src/firebase/functions";
import { Timestamp } from "firebase/firestore";
import { aiScenarioRepository } from "src/features/ai-coach/custom-scenarios/ai-scenario.repository";
import { AiScenario } from "src/features/ai-coach/custom-scenarios/entities/ai-scenario.entity";
import { aiSessionRepository } from "./repositories/ai-session.repository";
import { aiSessionTopicRepository } from "./repositories/ai-session-topic.repository";
import { createAISessionMessageRepository } from "./repositories/ai-session-message.repository";
import { wordBankRepository } from "./repositories/word-bank.repository";
import {
  AiSession,
  AiSessionMessage,
  AiSessionTopic,
  SessionContext,
  WordBankItem,
} from "./types/ai-session.entity";
import {
  getTopicContextCustomScenarioIds,
  getUniqueContextIds,
  normalizeAISession,
} from "./utils";

export type AISessionsFilter = Partial<
  Pick<
    AiSession,
    "userId" | "status"
  >
> & {
  aiSessionId?: string;
};

export type LearnerAISessionsFilter = {
  userId: string;
  startDate: Date;
  endDate: Date;
};

export type AISessionContextResources = {
  scenarios: (AiScenario & { id: string })[];
  words: (WordBankItem & { id: string })[];
};

const getEndDateExclusive = (date: Date) => {
  const nextDay = new Date(date);
  nextDay.setHours(0, 0, 0, 0);
  nextDay.setDate(nextDay.getDate() + 1);
  return Timestamp.fromDate(nextDay);
};

const aiSessionsService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getLearnerAISessions: builder.query<
      (AiSession & { id: string })[],
      LearnerAISessionsFilter
    >({
      queryFn: async ({ userId, startDate, endDate }) => {
        const result = await executeQuery(() =>
          aiSessionRepository.find({
            where: {
              userId,
              startTime: {
                $gte: Timestamp.fromDate(startDate),
                $lt: getEndDateExclusive(endDate),
              },
            },
            orderBy: ["startTime", "desc"],
            limit: 1000,
          } as any)
        );
        if ("error" in result) return { error: result.error } as any;

        return {
          data: ((result.data as unknown) as (AiSession & { id: string })[]).map(normalizeAISession),
        };
      },
      providesTags: ["AISession"],
    }),
    getAISessions: builder.query<(AiSession & { id: string })[], { where?: AISessionsFilter; offset?: string}>({
      queryFn: async ({ where = {}, offset }) => {
        // Direct AI session id lookup
        if (where.aiSessionId) {
          const { aiSessionId, ...rest } = where;
          const res = await executeQuery(() => aiSessionRepository.findById(aiSessionId!));
          if ("error" in res) return { error: res.error } as any;
          const aiSession = res.data as (AiSession & { id: string }) | null;
          if (!aiSession) return { data: [] } as any;
          const normalizedAISession = normalizeAISession(aiSession);
          // Match remaining filters client-side if provided
          const matches = Object.entries(rest).every(([k, v]) => (v == null ? true : (normalizedAISession as any)[k] === v));
          return { data: matches ? [normalizedAISession] : [] } as any;
        }

        const result = await executeQuery(() => aiSessionRepository.find({ where, offset, orderBy: ["startTime", "desc"] } as any));
        if ("error" in result) {
          return { error: result.error } as any;
        }
        return {
          data: ((result.data as unknown) as (AiSession & { id: string })[]).map(normalizeAISession),
        };
      },
      providesTags: ["AISession"],
    }),

    countAISessions: builder.query<number, { where?: AISessionsFilter }>({
      queryFn: async ({ where = {} }) => {
        if (where.aiSessionId) {
          const { aiSessionId, ...rest } = where;
          const res = await executeQuery(() => aiSessionRepository.findById(aiSessionId!));
          if ("error" in res) return { error: res.error } as any;
          const aiSession = res.data as AiSession | null;
          if (!aiSession) return { data: 0 } as any;
          const matches = Object.entries(rest).every(([k, v]) => (v == null ? true : (aiSession as any)[k] === v));
          return { data: matches ? 1 : 0 } as any;
        }
        return executeQuery(() => aiSessionRepository.count(where as any)) as any;
      },
      providesTags: ["AISession"],
    }),

    getAISessionById: builder.query<AiSession & { id: string }, string>({
      queryFn: async (id) => {
        const result = await executeQuery(() => aiSessionRepository.findById(id));
        if ("error" in result) return { error: result.error } as any;
        return { data: normalizeAISession((result.data as unknown) as AiSession & { id: string }) };
      },
      providesTags: ["AISession"],
    }),

    getAISessionTopics: builder.query<(AiSessionTopic & { id: string })[], { sessionId: string }>({
      queryFn: async ({ sessionId }) => {
        const result = await executeQuery(() => aiSessionTopicRepository.find({ where: { sessionId }, limit: 100 } as any));
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (AiSessionTopic & { id: string })[] };
      },
      providesTags: ["AISession"],
    }),

    getAISessionTopicsBySessionIds: builder.query<(AiSessionTopic & { id: string })[], { sessionIds: string[] }>({
      queryFn: async ({ sessionIds }) => {
        const cleanSessionIds = Array.from(new Set(sessionIds.filter(Boolean)));
        if (cleanSessionIds.length === 0) return { data: [] as (AiSessionTopic & { id: string })[] };

        const chunkSize = 10; // Firestore "in" supports up to 10 values
        const chunks: string[][] = [];
        for (let i = 0; i < cleanSessionIds.length; i += chunkSize) {
          chunks.push(cleanSessionIds.slice(i, i + chunkSize));
        }

        const allTopics: (AiSessionTopic & { id: string })[] = [];
        for (const chunk of chunks) {
          const result = await executeQuery(() =>
            aiSessionTopicRepository.find({
              where: { sessionId: { $in: chunk } } as any,
              limit: 1000,
            } as any)
          );

          if ("error" in result) return { error: result.error } as any;
          allTopics.push(...((result.data as unknown) as (AiSessionTopic & { id: string })[]));
        }

        return { data: allTopics };
      },
      providesTags: ["AISession"],
    }),

    getAISessionMessages: builder.query<(AiSessionMessage & { id: string })[], { sessionId: string }>({
      queryFn: async ({ sessionId }) => {
        const repository = createAISessionMessageRepository(sessionId);
        const result = await executeQuery(() => repository.findAllBySessionId(1000));
        if ("error" in result) return { error: result.error } as any;
        return { data: (result.data as unknown) as (AiSessionMessage & { id: string })[] };
      },
      providesTags: ["AISession"],
    }),

    getAISessionContextResources: builder.query<AISessionContextResources, SessionContext>({
      queryFn: async (context) => {
        const emptyResult: AISessionContextResources = { scenarios: [], words: [] };

        if (!context) {
          return { data: emptyResult };
        }

        if (context.sessionMode === "TOPIC") {
          const scenarioIds = getTopicContextCustomScenarioIds(context.items);
          if (scenarioIds.length === 0) {
            return { data: emptyResult };
          }

          const result = await executeQuery(() => aiScenarioRepository.findByIds(scenarioIds) as any);
          if ("error" in result) return { error: result.error } as any;

          return {
            data: {
              scenarios: (result.data as unknown) as (AiScenario & { id: string })[],
              words: [],
            },
          };
        }

        if (context.sessionMode === "WORD_BANK") {
          const wordIds = getUniqueContextIds(context.items);
          if (wordIds.length === 0) {
            return { data: emptyResult };
          }

          const result = await executeQuery(() => wordBankRepository.findByIds(wordIds) as any);
          if ("error" in result) return { error: result.error } as any;

          return {
            data: {
              scenarios: [],
              words: (result.data as unknown) as (WordBankItem & { id: string })[],
            },
          };
        }

        return { data: emptyResult };
      },
      providesTags: ["AISession"],
    }),

    getAISessionScenariosByIds: builder.query<(AiScenario & { id: string })[], { scenarioIds: string[] }>({
      queryFn: async ({ scenarioIds }) => {
        const cleanScenarioIds = Array.from(new Set(scenarioIds.filter(Boolean)));
        if (cleanScenarioIds.length === 0) {
          return { data: [] as (AiScenario & { id: string })[] };
        }

        const result = await executeQuery(() => aiScenarioRepository.findByIds(cleanScenarioIds) as any);
        if ("error" in result) return { error: result.error } as any;

        return {
          data: (result.data as unknown) as (AiScenario & { id: string })[],
        };
      },
      providesTags: ["AISession"],
    }),

    deactivateAISession: builder.mutation<{ success: boolean }, string>({
      queryFn: async (sessionId) => {
        try {
          const result = await callFunctionSafe<{ success: boolean }>("deactivateAISession", { sessionId });
          
          if ("data" in result && result.data.success) {
            return { data: { success: true } };
          } else {
            return { 
              error: { 
                status: 500, 
                data: "Failed to deactivate AI session" 
              } 
            };
          }
        } catch (error) {
          console.error("Error calling deactivateAISession:", error);
          return { 
            error: { 
              status: 500, 
              data: "Failed to deactivate AI session. Please try again." 
            } 
          };
        }
      },
      invalidatesTags: ["AISession"],
    }),

    refundAiSessionToUserByValue: builder.mutation<{ success: boolean; message: string }, { userId: string; value: number; sessionId: string }>({
      queryFn: async ({ userId, value, sessionId }) => {
        try {
          const result = await callFunctionSafe<{ success: boolean; message: string }>("refundAiSessionToUserByValue", { 
            userId, 
            value,
            sessionId
          });
          
          if ("data" in result && result.data.success) {
            return { data: { success: true, message: result.data.message } };
          } else {
            return { 
              error: { 
                status: 500, 
                data: "Failed to refund AI session" 
              } 
            };
          }
        } catch (error) {
          console.error("Error calling refundAiSessionToUserByValue:", error);
          return { 
            error: { 
              status: 500, 
              data: "Failed to refund AI session. Please try again." 
            } 
          };
        }
      },
      invalidatesTags: ["AISession", "UserTransaction"],
    }),
  }),
});

export const {
  useGetLearnerAISessionsQuery,
  useGetAISessionsQuery,
  useCountAISessionsQuery,
  useGetAISessionByIdQuery,
  useGetAISessionTopicsQuery,
  useGetAISessionTopicsBySessionIdsQuery,
  useGetAISessionMessagesQuery,
  useGetAISessionContextResourcesQuery,
  useGetAISessionScenariosByIdsQuery,
  useDeactivateAISessionMutation,
  useRefundAiSessionToUserByValueMutation,
} = aiSessionsService;
